import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { LocalStorage } from '@/types/enums';
import { Message } from '@/app/graphql/codegen';

interface PendingMessage {
  id: string;
  content: string;
  createdAt: string;
  type: 'USER';
  chatId: string;
  senderId: string;
  attachments: Array<{
    fileName: string;
    tokens: number;
    fileSize: number;
  }>;
}

interface ChatState {
  inputValues: Record<string, string>;
  pendingSentMessages: Record<string, PendingMessage[]>;
}

// Load saved chat input values from localStorage or use empty object if none exists
const getSavedInputValues = (): Record<string, string> => {
  const savedValues = localStorage.getItem(LocalStorage.CHAT_INPUT_VALUES);
  return savedValues ? JSON.parse(savedValues) : {};
};

const initialState: ChatState = {
  inputValues: getSavedInputValues(),
  pendingSentMessages: {},
};

const chatSlice = createSlice({
  name: 'chat',
  initialState,
  reducers: {
    setInputValue: (state, action: PayloadAction<{ chatId: string; value: string }>) => {
      const { chatId, value } = action.payload;
      state.inputValues[chatId] = value;
      
      // Save to localStorage
      localStorage.setItem(LocalStorage.CHAT_INPUT_VALUES, JSON.stringify(state.inputValues));
    },
    clearInputValue: (state, action: PayloadAction<string>) => {
      const chatId = action.payload;
      state.inputValues[chatId] = '';
      
      // Save to localStorage
      localStorage.setItem(LocalStorage.CHAT_INPUT_VALUES, JSON.stringify(state.inputValues));
    },
    addPendingSentMessage: (state, action: PayloadAction<{ chatId: string; message: PendingMessage }>) => {
      const { chatId, message } = action.payload;
      if (!state.pendingSentMessages[chatId]) {
        state.pendingSentMessages[chatId] = [];
      }
      state.pendingSentMessages[chatId].push(message);
    },
    removePendingSentMessage: (state, action: PayloadAction<{ chatId: string; messageId: string }>) => {
      const { chatId, messageId } = action.payload;
      if (state.pendingSentMessages[chatId]) {
        state.pendingSentMessages[chatId] = state.pendingSentMessages[chatId].filter(
          msg => msg.id !== messageId
        );
      }
    },
    reconcilePendingMessages: (state, action: PayloadAction<{ chatId: string; serverMessages: any[] }>) => {
      const { chatId, serverMessages } = action.payload;
      if (!state.pendingSentMessages[chatId]) return;
      
      // Remove pending messages that now exist on the server
      // Match by content + createdAt proximity (within 5 seconds) or exact ID if server echoed our temp ID
      state.pendingSentMessages[chatId] = state.pendingSentMessages[chatId].filter(pending => {
        const matchFound = serverMessages.some(serverMsg => {
          const normalizeText = (v: unknown) => (typeof v === 'string' ? v.trim() : '');
          const parseTimeMs = (v: unknown): number => {
            if (typeof v === 'number' && Number.isFinite(v)) return v;
            if (typeof v === 'string') {
              const s = v.trim();
              // backend sometimes returns epoch-ms as a string (e.g. "1765898628071")
              if (/^\d{12,}$/.test(s)) return Number(s);
              const t = Date.parse(s);
              return Number.isFinite(t) ? t : NaN;
            }
            return NaN;
          };

          // Exact ID match (if backend echoed our optimistic ID somehow)
          if (serverMsg.id === pending.id) return true;
          
          // Content + timestamp proximity match
          if (normalizeText(serverMsg.content) === normalizeText(pending.content) && serverMsg.type === 'USER') {
            const pendingTime = parseTimeMs(pending.createdAt);
            const serverTime = parseTimeMs(serverMsg.createdAt);
            if (!Number.isFinite(pendingTime) || !Number.isFinite(serverTime)) return false;
            const diffMs = Math.abs(serverTime - pendingTime);
            return diffMs < 10_000; // Within 10 seconds (more tolerant across formats)
          }
          
          return false;
        });
        
        return !matchFound; // Keep if NOT matched (still pending)
      });
    },
    clearPendingSentMessages: (state, action: PayloadAction<string>) => {
      const chatId = action.payload;
      delete state.pendingSentMessages[chatId];
    },
  },
});

export const { 
  setInputValue, 
  clearInputValue,
  addPendingSentMessage,
  removePendingSentMessage,
  reconcilePendingMessages,
  clearPendingSentMessages,
} = chatSlice.actions;
export default chatSlice.reducer; 